import { combineEpics, ofType } from 'redux-observable';
import { EMPTY } from 'rxjs';
import { concatMap, filter, flatMap, take, takeUntil } from 'rxjs/operators';
import { pushToDataLayer } from '../../common/analytics.js';
import { handleOrderPurchaseAnalytics } from '../../components/EoeCheckout/checkoutAnalyticsUtils';
import { CreditCheckDecision } from '../../generated/api/models.js';
import { TypeKeys } from '../actions/index.js';
import * as DL from '../common/analyticsDataLayerEvent.js';
import { isGoogleEcommerceAction } from '../common/googleEcommerceEvent.js';
import type { EpicDependencies } from './epicUtils.js';
import type {
  ActionTypes,
  AddCartUpdateEventToAnalyticsAction,
  GoogleEcommerceActionTypes,
  PollForCreditCheckV2FulfilledAction,
  SubmitOnlineOrderAction,
  SubmitOrderFulfilledAction,
} from '../actions/index.js';
import type { State } from '../common/store.js';
import type { Action } from 'redux';
import type { ActionsObservable, Epic } from 'redux-observable';

const isPassableDecision = (action: PollForCreditCheckV2FulfilledAction) => {
  return action.response.decision === CreditCheckDecision.PASS;
};

/*
 * This chains:
 * - submit online order (Used for order items)
 * - submit online order fulfilled (Used for order ID)
 * - credit check fulfilled (Used for checking that order is OK)
 */
export const createGoogleEcommercePurchaseActionFromEndWizardActionEpic: Epic<
  ActionTypes,
  Action,
  State,
  EpicDependencies
> = (action$: ActionsObservable<ActionTypes>) => {
  return action$.pipe(
    // Wait for a Submit online order
    ofType(TypeKeys.SUBMIT_ONLINE_ORDER),
    concatMap((submitOrderAction: SubmitOnlineOrderAction) => {
      return action$.pipe(
        // Wait for a Submit online order fulfilled
        ofType(TypeKeys.SUBMIT_ONLINE_ORDER_FULFILLED),
        take(1),
        concatMap((submitOrderFulfilledAction: SubmitOrderFulfilledAction) => {
          return action$.pipe(
            // Wait for a Credit check fulfilled
            ofType(TypeKeys.POLL_FOR_CREDIT_CHECK_V2_FULFILLED),
            take(1),
            concatMap((creditCheckFulfilledAction: PollForCreditCheckV2FulfilledAction) => {
              // If credit check passed, send ecommerce purchase action
              if (isPassableDecision(creditCheckFulfilledAction)) {
                handleOrderPurchaseAnalytics(submitOrderAction.items, submitOrderFulfilledAction.orderId);
              }
              return EMPTY;
            }),
            // 'Reset' on credit check failure and logout
            takeUntil(action$.ofType(TypeKeys.POLL_FOR_CREDIT_CHECK_FAILED, TypeKeys.CLEAR_STATE))
          );
        }),
        // 'Reset' on submit failure
        takeUntil(action$.ofType(TypeKeys.SUBMIT_ONLINE_ORDER_FAILED))
      );
    })
  );
};

export const pushGoogleEcommerceEventToDataLayerEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>
) => {
  return action$.pipe(
    filter((action: ActionTypes) => isGoogleEcommerceAction(action)),
    flatMap((action: GoogleEcommerceActionTypes) => {
      pushToDataLayer(action.event);
      return EMPTY;
    })
  );
};

export const pushAnalyticsEventToDataLayerEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>
) => {
  return action$.pipe(
    filter((action: ActionTypes) => DL.isAnalyticsDataLayerAction(action)),
    flatMap((action: ActionTypes) => {
      pushToDataLayer(DL.getAnalyticsDataLayerEvent(action));
      return EMPTY;
    })
  );
};

export const pushUpdateCartEventToDataLayerEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>
) => {
  return action$.pipe(
    ofType(TypeKeys.ADD_CART_EVENT_UPDATE_TO_ANALYTICS),
    concatMap((action: AddCartUpdateEventToAnalyticsAction) => {
      if (action.cartUpdateEventData && action.cartUpdateEventData.length > 0) {
        action.cartUpdateEventData.forEach(item => {
          if (item.deltaQuantity !== 0) {
            pushToDataLayer({
              action: item.analyticsEventType,
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'delta-quantity': item.deltaQuantity,
              event: item.event,
              target: item.target,
              // eslint-disable-next-line @typescript-eslint/naming-convention
              'target-properties': item.onlineModelCode,
            });
          }
        });
      }
      return EMPTY;
    })
  );
};

export const analyticsEpic: Epic<ActionTypes, Action, State, EpicDependencies> = combineEpics(
  createGoogleEcommercePurchaseActionFromEndWizardActionEpic,
  pushGoogleEcommerceEventToDataLayerEpic,
  pushAnalyticsEventToDataLayerEpic,
  pushUpdateCartEventToDataLayerEpic
);
